import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Breadcrumbs,
  Link,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  QuestionAnswer as QuestionAnswerIcon,
  VideoLibrary as VideoLibraryIcon,
  MenuBook as MenuBookIcon,
  Email as EmailIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

// FAQ interface
interface FAQ {
  question: string;
  answer: string;
  category: string;
}

// FAQ data
const faqs: FAQ[] = [
  {
    question: "How do I get started with SharkPanzer?",
    answer: "Getting started is easy! Simply create an account, paste a YouTube video URL in the input field on the main page, select your preferred output format and language, and click 'Create Capsule'. Your knowledge capsule will be generated within minutes.",
    category: "Getting Started"
  },
  {
    question: "What types of videos work best with SharkPanzer?",
    answer: "SharkPanzer works well with educational content, lectures, tutorials, documentaries, podcasts, interviews, and informational videos. Videos with clear speech and structured content yield the best results. However, our AI is designed to handle a wide variety of content types.",
    category: "Getting Started"
  },
  {
    question: "Is there a limit to how many capsules I can create?",
    answer: "Yes, limits depend on your subscription plan. Free users can create up to 5 capsules per month, Basic subscribers get 50 capsules per month, and Premium subscribers enjoy unlimited capsules. You can view your current usage in your account dashboard.",
    category: "Usage & Limits"
  },
  {
    question: "What is the maximum video length SharkPanzer can process?",
    answer: "The maximum video length depends on your subscription plan. Free users can process videos up to 20 minutes, Basic subscribers up to 60 minutes, and Premium subscribers up to 3 hours (180 minutes).",
    category: "Usage & Limits"
  },
  {
    question: "How accurate are the capsules generated by SharkPanzer?",
    answer: "SharkPanzer uses advanced AI models to ensure high accuracy. However, accuracy can vary depending on factors like audio quality, speech clarity, and content complexity. We continuously improve our models to enhance accuracy. If you notice any inaccuracies, you can provide feedback directly from the capsule page.",
    category: "Quality & Features"
  },
  {
    question: "What languages does SharkPanzer support?",
    answer: "SharkPanzer supports multiple languages including English, Hindi, Hinglish (Hindi-English mix), Brazilian Portuguese, Brazinglish (Brazilian-English mix), and more. We're constantly adding support for new languages and regional variants.",
    category: "Quality & Features"
  },
  {
    question: "Can I download or share my capsules?",
    answer: "Yes, Basic and Premium subscribers can download capsules in various formats (PDF, Markdown, etc.). All users can share capsules via a unique link, but the recipient may need a SharkPanzer account to access the shared content, depending on your privacy settings.",
    category: "Usage & Limits"
  },
  {
    question: "How do I cancel my subscription?",
    answer: "You can cancel your subscription at any time from your account settings. Navigate to 'Account' > 'Subscription' and click 'Cancel Subscription'. You'll continue to have access to your paid features until the end of your current billing cycle.",
    category: "Billing & Account"
  },
  {
    question: "What is your refund policy?",
    answer: "We offer a 3-day money-back guarantee for all paid plans, provided you have used less than 10% of your subscription credits. If you're not satisfied with our service, you can request a refund within 3 days of your initial purchase. For more details, please visit our Refund Policy page.",
    category: "Billing & Account"
  },
  {
    question: "How secure is my data on SharkPanzer?",
    answer: "We take data security very seriously. All data is encrypted in transit and at rest. We do not share your personal information with third parties without your consent. For more information, please review our Privacy Policy.",
    category: "Privacy & Security"
  },
  {
    question: "Can I use SharkPanzer for videos in languages other than English?",
    answer: "Yes, SharkPanzer can process videos in multiple languages. Our AI models are trained on diverse linguistic data. For best results with non-English content, select the appropriate input language when creating your capsule.",
    category: "Quality & Features"
  },
  {
    question: "What should I do if I encounter an error?",
    answer: "If you encounter an error, first try refreshing the page or restarting the process. If the issue persists, check our status page for any ongoing service disruptions. For further assistance, contact our support team with details about the error, including any error messages and the steps that led to the issue.",
    category: "Troubleshooting"
  }
];

const SupportPage: React.FC = () => {
  // State for search query
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Filter FAQs based on search query
  const filteredFaqs = searchQuery.trim() === '' 
    ? faqs 
    : faqs.filter(faq => 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
  
  // Group FAQs by category
  const faqsByCategory: Record<string, FAQ[]> = {};
  filteredFaqs.forEach(faq => {
    if (!faqsByCategory[faq.category]) {
      faqsByCategory[faq.category] = [];
    }
    faqsByCategory[faq.category].push(faq);
  });
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link component={RouterLink} to="/" color="inherit">
          Home
        </Link>
        <Typography color="text.primary">Support</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Support Center
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          Find answers to common questions, troubleshooting guides, and resources to help you get the most out of SharkPanzer. SharkPanzer is a product of UnwiringTech®. Unwiring Tech GSTIN- 09CYNPA1063Q1ZZ.
        </Typography>
      </Box>
      
      {/* Search Bar */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 3,
          mb: 6,
          borderRadius: 4
        }}
      >
        <TextField
          fullWidth
          placeholder="Search for answers..."
          variant="outlined"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
            sx: { 
              borderRadius: 4,
              fontSize: '1.1rem'
            }
          }}
        />
      </Paper>
      
      {/* Support Options */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
        How Can We Help You?
      </Typography>
      
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0} 
            className="glass-container"
            sx={{ 
              height: '100%',
              borderRadius: 4,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <QuestionAnswerIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                FAQs
              </Typography>
              <Typography variant="body2" paragraph>
                Browse our frequently asked questions to find quick answers.
              </Typography>
              <Button 
                variant="outlined" 
                color="primary"
                size="small"
                sx={{ borderRadius: 4 }}
                onClick={() => window.scrollTo({ top: document.getElementById('faqs')?.offsetTop || 0 - 100, behavior: 'smooth' })}
              >
                View FAQs
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0} 
            className="glass-container"
            sx={{ 
              height: '100%',
              borderRadius: 4,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <VideoLibraryIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Video Tutorials
              </Typography>
              <Typography variant="body2" paragraph>
                Watch step-by-step guides on how to use SharkPanzer.
              </Typography>
              <Button 
                variant="outlined" 
                color="primary"
                size="small"
                component={RouterLink}
                to="/tutorials"
                sx={{ borderRadius: 4 }}
              >
                Watch Tutorials
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0} 
            className="glass-container"
            sx={{ 
              height: '100%',
              borderRadius: 4,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <MenuBookIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                User Guide
              </Typography>
              <Typography variant="body2" paragraph>
                Explore our comprehensive documentation and guides.
              </Typography>
              <Button 
                variant="outlined" 
                color="primary"
                size="small"
                component={RouterLink}
                to="/guide"
                sx={{ borderRadius: 4 }}
              >
                Read Guide
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0} 
            className="glass-container"
            sx={{ 
              height: '100%',
              borderRadius: 4,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <EmailIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Contact Support
              </Typography>
              <Typography variant="body2" paragraph>
                Need personalized help? Reach out to our support team.
              </Typography>
              <Button 
                variant="outlined" 
                color="primary"
                size="small"
                component={RouterLink}
                to="/contact"
                sx={{ borderRadius: 4 }}
              >
                Contact Us
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Live Chat Banner */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4,
          mb: 6,
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.2) 0%, rgba(0, 150, 199, 0.2) 100%)',
          border: '1px solid',
          borderColor: 'primary.main'
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ChatIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
              <Box>
                <Typography variant="h5" gutterBottom>
                  Need Immediate Assistance?
                </Typography>
                <Typography variant="body1">
                  Our support team is available for live chat 24/7, Monday to Sunday. Get instant help with any questions or issues you may have.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ChatIcon />}
              sx={{ 
                borderRadius: 4,
                px: 3,
                py: 1.5
              }}
            >
              Start Live Chat
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* FAQs Section */}
      <Box id="faqs" sx={{ mb: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
          Frequently Asked Questions
        </Typography>
        
        {Object.keys(faqsByCategory).length > 0 ? (
          Object.entries(faqsByCategory).map(([category, categoryFaqs]) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" component="h3">
                  {category}
                </Typography>
                <Chip 
                  label={`${categoryFaqs.length} ${categoryFaqs.length === 1 ? 'question' : 'questions'}`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              </Box>
              
              {categoryFaqs.map((faq, index) => (
                <Accordion 
                  key={index}
                  elevation={0}
                  sx={{ 
                    mb: 2,
                    backgroundColor: 'rgba(30, 30, 30, 0.6)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '8px !important',
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: '0 0 16px 0',
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      borderRadius: 2,
                      '&.Mui-expanded': {
                        minHeight: 48,
                      }
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))
        ) : (
          <Paper 
            elevation={0} 
            className="glass-container"
            sx={{ 
              p: 4,
              borderRadius: 4,
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" gutterBottom>
              No results found
            </Typography>
            <Typography variant="body1" paragraph>
              We couldn't find any FAQs matching your search query. Please try different keywords or contact our support team for assistance.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/contact"
              sx={{ 
                mt: 2,
                borderRadius: 4
              }}
            >
              Contact Support
            </Button>
          </Paper>
        )}
      </Box>
      
      {/* Still Need Help Section */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          borderRadius: 4,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          Still Need Help?
        </Typography>
        <Typography variant="body1" paragraph sx={{ maxWidth: 700, mx: 'auto' }}>
          If you couldn't find the answer you were looking for, our support team is ready to assist you. Contact us through any of the following channels:
        </Typography>
        
        <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              component={RouterLink}
              to="/contact"
              startIcon={<EmailIcon />}
              sx={{ borderRadius: 4 }}
            >
              Email Support
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ChatIcon />}
              sx={{ borderRadius: 4 }}
            >
              Live Chat
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default SupportPage;
